import React, { useCallback, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, Link2, LogOut, Megaphone, MessageCircle, ScrollText, Settings, Users } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/components/AuthProvider';
import { useNavigation } from '@/contexts/NavigationContext';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useSidebar } from '@/components/ui/sidebar';
import { isSubscriptionExpiredOrCancelled } from '@/lib/subscription-utils';
import { hasAnyEntitledIntegration } from '@/lib/integrationEntitlements';
import { cn } from '@/lib/utils';

type AccountLink = {
  label: string;
  url: string;
  featureKey: string | null;
};

/** Account settings opens a dedicated page (left-nav shell). Sub-items live there. */
export const ACCOUNT_SETTINGS_URL = '/home/account-settings';
/** Billing overview in the new shell. Never `/home/billing` (legacy page). */
export const ACCOUNT_BILLING_URL = `${ACCOUNT_SETTINGS_URL}?section=billing&interval=monthly`;
/** Plans catalog in the new shell. Never `/home/plans` or the BillingDialog. */
export const ACCOUNT_PLANS_URL = `${ACCOUNT_SETTINGS_URL}?section=plans&interval=monthly`;
/** Business accounts in the new shell. Never `/home/business-accounts` (legacy page). */
export const ACCOUNT_BUSINESS_ACCOUNTS_URL = `${ACCOUNT_SETTINGS_URL}?section=business-accounts`;
/** Changelog page. Moved out of the Support sidebar accordion 2026-08-21. */
export const ACCOUNT_UPDATES_URL = '/home/updates';
/** Getting-started guides. Moved out of the Support sidebar accordion 2026-08-21. */
export const ACCOUNT_GETTING_STARTED_URL = '/home/how-to-use';

const TOP_LINKS: AccountLink[] = [
  { label: 'Integrations', url: '/home/integrations', featureKey: 'integrations' },
  { label: 'Team management', url: '/home/team-management', featureKey: 'team_management' },
  // Internal team chat (TeamChat.tsx, /home/team-chat). Duplicates the
  // Messaging tab already inside Team Management, so it's suppressed below
  // (in `topLinks`, not here) for any role that can already open Team
  // Management — this menu must never list both at once. Same rule as the
  // Account Settings accordion in AppSidebar.tsx.
  { label: 'Team Chat', url: '/home/team-chat', featureKey: null },
  { label: 'Audit log', url: '/home/audit-log', featureKey: null },
];

const TOP_LINK_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  '/home/integrations': Link2,
  '/home/team-management': Users,
  '/home/team-chat': MessageCircle,
  '/home/audit-log': ScrollText,
};

/**
 * Must stay in lockstep with `ROUTE_ROLE_RESTRICTIONS` in AppSidebar for these
 * three paths. USER's allowedByRole list includes integrations / team-management
 * but the route gate is ADMIN-only — this object is what actually hides them.
 */
const ACCOUNT_MENU_ROLE_RESTRICTIONS: Record<string, string[]> = {
  '/home/audit-log': ['ADMIN'],
  '/home/integrations': ['ADMIN'],
  '/home/team-management': ['ADMIN'],
};

/**
 * Role → account-menu URLs, mirroring AppSidebar `allowedByRole` for these
 * paths only. `ACCOUNT_MENU_ROLE_RESTRICTIONS` still wins.
 *
 * `/home/team-chat` is included for ADMIN/USER/AGENT (not RIDER) to match
 * `allowedByRole` in AppSidebar.tsx — the `topLinks` exclusion filter above
 * still hides it from whichever role can already open Team Management, so
 * listing it here for ADMIN is harmless (never independently reachable).
 * Do NOT drop it again if this list is ever rebuilt from scratch.
 */
const ACCOUNT_MENU_URLS_BY_ROLE: Record<string, string[]> = {
  ADMIN: [
    ACCOUNT_SETTINGS_URL,
    '/home/integrations',
    '/home/team-management',
    '/home/audit-log',
    '/home/team-chat',
  ],
  USER: [ACCOUNT_SETTINGS_URL, '/home/integrations', '/home/team-management', '/home/team-chat'],
  AGENT: [ACCOUNT_SETTINGS_URL, '/home/team-chat'],
  RIDER: [ACCOUNT_SETTINGS_URL],
};

function userInitials(fullName: string | undefined, email: string | undefined): string {
  if (fullName?.trim()) {
    return fullName
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email?.[0]?.toUpperCase() || 'U';
}

const menuItemClass =
  'flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 font-medium text-[#344054] hover:bg-[#F9FAFB]';

function useAccountMenuCanOpen() {
  const { user } = useAuth();
  const { data: entitlements, isError: entitlementsError } = useOrganizationEntitlements();
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    isError: isSubscriptionError,
  } = useCurrentSubscription();

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isOwner = !!(user as any)?.is_organization_owner;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
  const currentRole = isAdmin
    ? 'ADMIN'
    : roleRaw === 'AGENT'
      ? 'AGENT'
      : roleRaw === 'RIDER'
        ? 'RIDER'
        : 'USER';

  const isSubscriptionExpired =
    !isLoadingSubscription &&
    !isSubscriptionError &&
    (!currentSubscription || isSubscriptionExpiredOrCancelled(currentSubscription));

  const baseFeatures = entitlements?.entitlements?.features || {};
  const hasEnabledIntegrationConnector = useMemo(
    () =>
      hasAnyEntitledIntegration({
        hasEntitlements: Boolean(entitlements) && !entitlementsError,
        features: baseFeatures,
        subFeatures: entitlements?.entitlements?.sub_features?.integrations,
        limits: entitlements?.entitlements?.limits,
      }),
    [entitlements, entitlementsError, baseFeatures],
  );

  const isFeatureEnabled = useCallback(
    (featureKey: string | null, url?: string): boolean => {
      if (url === '/home/integrations') return hasEnabledIntegrationConnector;
      if (!featureKey) return true;
      if (featureKey === 'integrations' || featureKey === 'billing') return true;
      return baseFeatures[featureKey] === true;
    },
    [baseFeatures, hasEnabledIntegrationConnector],
  );

  const urlsForRole = ACCOUNT_MENU_URLS_BY_ROLE[currentRole] ?? ACCOUNT_MENU_URLS_BY_ROLE.USER;

  const canOpen = useCallback(
    (url: string, featureKey: string | null) => {
      const path = url.split('?')[0];
      if (isSubscriptionExpired) return isFeatureEnabled(featureKey, path);
      const restricted = ACCOUNT_MENU_ROLE_RESTRICTIONS[path];
      const roleOk = !restricted || restricted.includes(currentRole);
      return urlsForRole.includes(path) && isFeatureEnabled(featureKey, path) && roleOk;
    },
    [isSubscriptionExpired, isFeatureEnabled, urlsForRole, currentRole],
  );

  // Match App.tsx RoleRoute for /home/updates: ADMIN / USER / AGENT.
  // Not routed through canOpen — that helper bypasses role on an expired
  // subscription, which would surface the changelog to a Rider who cannot
  // open the page. Support is also always-allowed (ALWAYS_ALLOWED_SECTION_IDS),
  // so custom menu_permissions must never hide this.
  const showWhatsNew = currentRole !== 'RIDER';

  // Match App.tsx RoleRoute for /home/how-to-use: ADMIN only. Same canOpen
  // bypass as Updates — a USER/AGENT/Rider on an expired plan must not see
  // a row that RoleRoute would bounce.
  const showGettingStarted = currentRole === 'ADMIN';

  return { canOpen, showWhatsNew, showGettingStarted };
}

export function SidebarAccountFooter() {
  const { user, signOut } = useAuth();
  const { interceptedNavigate } = useNavigation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { data: accounts } = useBusinessAccounts();
  const { selectedAccountId } = useBusinessAccountContext();
  const { canOpen, showWhatsNew, showGettingStarted } = useAccountMenuCanOpen();
  const [open, setOpen] = useState(false);

  const selectedAccount = useMemo(
    () => accounts?.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );

  const topLinks = TOP_LINKS.filter((item) => canOpen(item.url, item.featureKey)).filter(
    (item) =>
      // Team Chat duplicates Team Management's own Messaging tab — hide it
      // whenever Team Management is also offered in this menu.
      !(item.url === '/home/team-chat' && canOpen('/home/team-management', 'team_management')),
  );
  const showSettings = canOpen(ACCOUNT_SETTINGS_URL, null);

  const displayName = user?.full_name?.trim() || user?.email || 'User';
  const initials = userInitials(user?.full_name, user?.email);
  // Same subtitle the header profile used: connected BA number, then the
  // user's own phone (phone-first accounts), then a clear empty state.
  const displayPhone =
    selectedAccount?.phone_number || user?.phone || 'No connected number';

  const closeAndNavigate = (url: string) => {
    setOpen(false);
    interceptedNavigate(url);
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut?.();
    interceptedNavigate('/auth');
  };

  const menu = (
    <>
      {(showSettings || topLinks.length > 0 || showGettingStarted || showWhatsNew) && (
        <div className="px-2 py-2">
          {showSettings && (
            <button
              type="button"
              data-testid="sidebar-account-settings"
              onClick={() => closeAndNavigate(ACCOUNT_SETTINGS_URL)}
              className={menuItemClass}
            >
              <Settings className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Account settings</span>
            </button>
          )}
          {topLinks.map((item) => {
            const LinkIcon = TOP_LINK_ICONS[item.url] ?? Users;
            return (
              <button
                type="button"
                key={item.url}
                data-testid={item.url === '/home/audit-log' ? 'sidebar-audit-log' : undefined}
                onClick={() => closeAndNavigate(item.url)}
                className={menuItemClass}
              >
                <LinkIcon className="h-4 w-4 text-[#667085]" />
                {item.label}
              </button>
            );
          })}
          {showGettingStarted && (
            <button
              type="button"
              data-testid="user-menu-getting-started"
              onClick={() => closeAndNavigate(ACCOUNT_GETTING_STARTED_URL)}
              className={menuItemClass}
            >
              <BookOpen className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Guide</span>
            </button>
          )}
          {showWhatsNew && (
            <button
              type="button"
              data-testid="user-menu-updates"
              onClick={() => closeAndNavigate(ACCOUNT_UPDATES_URL)}
              className={menuItemClass}
            >
              <Megaphone className="h-4 w-4 text-[#667085]" />
              <span className="flex-1 text-left">Updates</span>
            </button>
          )}
        </div>
      )}

      <div className="border-t border-[#EAECF0] px-2 py-2">
        <button
          type="button"
          data-testid="user-menu-logout"
          onClick={() => {
            void handleSignOut();
          }}
          className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 font-medium text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="sidebar-account-footer" className="flex-shrink-0 min-w-0">
      <HoverCard
        open={open}
        onOpenChange={setOpen}
        openDelay={80}
        closeDelay={220}
      >
        <HoverCardTrigger asChild>
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label={`${displayName} account menu`}
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 sm:gap-2.5 rounded-xl text-left transition-colors hover:bg-[#F9FAFB] outline-none px-1.5 py-1 sm:px-2 sm:py-1.5"
          >
            <Avatar className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 !rounded-[10px]">
              <AvatarImage src={user?.avatar_url} alt={displayName} />
              <AvatarFallback className="text-[12px] sm:text-[13px] font-semibold !rounded-[10px] bg-[#1D2939] text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="hidden sm:block min-w-0 max-w-[100px] md:max-w-[140px] lg:max-w-[180px]">
              <div className="truncate text-[14px] font-semibold text-[#101828]">{displayName}</div>
              <div className="truncate text-[13px] text-[#667085]">{displayPhone}</div>
            </div>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-[#98A2B3] transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="w-[280px] overflow-visible rounded-2xl border border-[#EAECF0] bg-white p-0 text-[14px] shadow-[0_12px_32px_rgba(16,24,40,0.12)] z-[80]"
        >
          {menu}
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}
